import React, { Component } from "react";
import { GlobalStateContext } from "./GlobalStateContext";
import { getCharacterList, getConstants } from "../services/characters";
import { getAudioList } from "../services/audios";
import { WEBSOCKET_ENDPOINT } from "../config";
import MobileDetect from "mobile-detect";
const uuid = require("uuid");

class GlobalStateProvider extends Component {
  constructor(props) {
    super(props);

    this.socket = null;
    this.state = {
      wwsToken: uuid.v4(),
      isMobile: false,
      listCharacteristics: null,
      listSkills: null,
      listAudios: null,
      audioStreaming: null,
      diceRollMessages: [],
      sessionInfo: {
        token: null,
        user: null,
        characters: null,
        activeCharacter: null,
        fetchUser: async () => {
          const token = localStorage.getItem("token");
          if (token !== null && this.state.sessionInfo.token !== token) {
            await this.fetchData();
          }
        },
      },
    };
  }

  handleWindowSize = () => {
    const md = new MobileDetect(window.navigator.userAgent);
    const isThisDeviceMobile = md.mobile() !== null || md.tablet() !== null;
    this.setState({ ...this.state, isMobile: isThisDeviceMobile });
  };

  componentDidMount() {
    this.fetchData(); // fetchData now calls fetchConstants internally
    this.fetchAudioList(); // Load initial audio list
    this.initSocket();
    this.updateDiceRollMessages();
    this.handleWindowSize();
    window.addEventListener("resize", this.handleWindowSize);
  }

  async fetchAudioList() {
    try {
      const audioList = await getAudioList();
      this.setState((prevState) => ({
        ...prevState,
        listAudios: audioList,
      }));
      console.log("Audio list loaded:", audioList?.length || 0);
    } catch (error) {
      console.error("Error fetching audio list:", error);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleWindowSize);
  }

  async fetchData() {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const characterData = await getCharacterList();
        console.log("Character data received:", characterData);
        this.setState((prevState) => ({
          sessionInfo: {
            ...prevState.sessionInfo,
            token: token,
            user: characterData.user,
            characters: characterData.characters,
            activeCharacter: null,
          },
        }));
        console.log(
          "Characters loaded:",
          characterData.characters?.length || 0,
        );

        // Fetch constants after successful login/user fetch
        await this.fetchConstants();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      localStorage.removeItem("token");
      this.setState((prevState) => ({
        sessionInfo: {
          ...prevState.sessionInfo,
          token: null,
          user: null,
          characters: null,
          activeCharacter: null,
        },
      }));
    }
  }

  async fetchConstants() {
    try {
      // Check localStorage for existing constants
      const storedCharacteristics = localStorage.getItem("listCharacteristics");
      const storedSkills = localStorage.getItem("listSkills");

      // Determine what we need to fetch
      const needed = [];
      if (!storedCharacteristics) needed.push("listCharacteristics");
      if (!storedSkills) needed.push("listSkills");

      // If we have everything in localStorage, use it
      if (needed.length === 0) {
        console.log("Loaded constants from localStorage");
        this.setState((prevState) => ({
          ...prevState,
          listCharacteristics: JSON.parse(storedCharacteristics),
          listSkills: JSON.parse(storedSkills),
        }));
        return;
      }

      // Fetch only what's missing
      console.log("Fetching missing constants from API:", needed);
      const constants = await getConstants(needed);

      // Update state and localStorage
      this.setState((prevState) => {
        const newState = { ...prevState };

        if (constants.listCharacteristics) {
          localStorage.setItem(
            "listCharacteristics",
            JSON.stringify(constants.listCharacteristics),
          );
          newState.listCharacteristics = constants.listCharacteristics;
        } else if (storedCharacteristics) {
          newState.listCharacteristics = JSON.parse(storedCharacteristics);
        }

        if (constants.listSkills) {
          localStorage.setItem(
            "listSkills",
            JSON.stringify(constants.listSkills),
          );
          newState.listSkills = constants.listSkills;
        } else if (storedSkills) {
          newState.listSkills = JSON.parse(storedSkills);
        }

        return newState;
      });
      console.log("Constants loaded and cached");
    } catch (error) {
      console.error("Error fetching constants:", error);
    }
  }

  initSocket() {
    const instanceSocket = new WebSocket(WEBSOCKET_ENDPOINT);

    instanceSocket.onopen = (ws) => {
      console.log("WebSocket connection established.", this.state.wwsToken);
      this.socket = ws.target;
      this.socket.send(
        JSON.stringify({
          action: "SESSION_TOKEN",
          sessionToken: this.state.wwsToken,
        }),
      );
    };

    instanceSocket.onerror = (error) => {
      console.error("WebSocket connection error:", error);
      this.socket = null;
    };

    instanceSocket.onclose = (event) => {
      console.log("WebSocket connection closed:", event);
      setTimeout(() => {
        this.initSocket();
      }, 1000);
    };

    instanceSocket.onmessage = (event) => {
      try {
        const parsedMessage = JSON.parse(event.data);
        console.log("WebSocket message received:", parsedMessage.action);
        if (parsedMessage.action === "audioStreaming") {
          this.setState({
            ...this.state,
            audioStreaming: parsedMessage.data.file,
          });
          console.log("Updated audioStreaming in GlobalStateProvider.js");
        }
        if (parsedMessage.action === "audioListUpdated") {
          this.setState({
            ...this.state,
            listAudios: parsedMessage.data.listAudios,
          });
          console.log(
            "Updated listAudios in GlobalStateProvider.js:",
            parsedMessage.data.listAudios?.length || 0,
          );
        }
        if (parsedMessage.action === "ping") {
          this.socket.send(
            JSON.stringify({
              sessionToken: this.state.wwsToken,
              action: "SESSION_PONG",
            }),
          );
        }
        if (parsedMessage.action === "diceRollMessage") {
          const now = new Date().getTime();
          const prevMessages = this.state.diceRollMessages || [];
          const newMessages = [
            ...prevMessages.filter((m) => m.visible && m.timeout >= now),
            {
              ...parsedMessage.data,
              timeout: now + 122220000,
              visible: true,
            },
          ];
          this.setState({
            ...this.state,
            diceRollMessages: newMessages,
          });
          console.log("Updated diceRollMessages in GlobalStateProvider.js");
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };
  }

  updateDiceRollMessages() {
    setInterval(() => {
      const { diceRollMessages } = this.state;
      const prevMessages = diceRollMessages || [];
      if (prevMessages.length > 0 && diceRollMessages.length > 0) {
        const now = new Date().getTime();
        const newRollMessages = prevMessages
          .filter((m) => m.visible)
          .map((m) => ({ ...m, visible: m.timeout >= now }));

        this.setState({
          ...this.state,
          diceRollMessages: newRollMessages,
        });
      }
    }, 1000);
  }

  render() {
    const {
      isMobile,
      sessionInfo,
      listCharacteristics,
      listSkills,
      listAudios,
      audioStreaming,
      diceRollMessages,
    } = this.state;
    const { children } = this.props;
    const contextValue = {
      isMobile,
      sessionInfo,
      listCharacteristics,
      listSkills,
      listAudios,
      audioStreaming,
      diceRollMessages,
      socket: this.socket === null ? false : true,
      sendSocketMessage: (action, message) => {
        if (this.socket !== null) {
          this.socket.send(
            JSON.stringify({
              sessionToken: this.state.wwsToken,
              action: action,
              data: message,
            }),
          );
        }
      },
      updateCharacter: (updateCharacter) =>
        this.setState({
          ...this.state,
          sessionInfo: {
            ...this.state.sessionInfo,
            characters: this.state.sessionInfo.characters.map((character) =>
              character._id === updateCharacter._id
                ? updateCharacter
                : character,
            ),
          },
        }),
      reloadCharacters: () =>
        getCharacterList().then((characterData) => {
          this.setState({
            ...this.state,
            sessionInfo: {
              ...this.state.sessionInfo,
              characters: characterData.characters,
            },
          });
        }),
      setSessionInfo: (newSessionInfo) =>
        this.setState({ ...this.state, sessionInfo: newSessionInfo }),
      setListAudios: (value) =>
        this.setState({ ...this.stats, listAudios: value }),
      setDiceRollMessages: (value) =>
        this.setState({ ...this.stats, diceRollMessages: value }),
    };

    return (
      <GlobalStateContext.Provider value={contextValue}>
        {children}
      </GlobalStateContext.Provider>
    );
  }
}

export default GlobalStateProvider;
